import type { UserPreference, UserThemePreference } from "~/types/User";

export const useUserPreferenceStore = defineStore(
  "userPreferenceStore",
  () => {
    const preference = ref<UserPreference>({ table: {} });

    function hasTablePreference(tableName: string) {
      return Object.prototype.hasOwnProperty.call(preference.value.table ?? {}, tableName);
    }

    function normalizePreference(newPreference?: Partial<UserPreference> | null): UserPreference {
      return {
        ...newPreference,
        table: newPreference?.table ?? {},
      };
    }

    function setPreference(newPreference: UserPreference) {
      preference.value = normalizePreference(newPreference);
    }
    function updateTablePreference(tableName: string, newPreference: object) {
      if (preference.value?.table === undefined) {
        preference.value.table = {};
      }
      if (hasTablePreference(tableName)) {
        preference.value.table[tableName] = {
          ...preference.value.table[tableName],
          ...toValue(newPreference),
        };
      }
      else {
        preference.value.table[tableName] = {
          ...toValue(newPreference),
        };
      }
    }
    function removeTablePreference(tableName: string) {
      if (!hasTablePreference(tableName)) {
        return;
      }

      const {
        [tableName]: _removedPreference,
        ...remainingPreferences
      } = preference.value.table;
      preference.value.table = remainingPreferences;
    }
    function getTablePreference(tableName: string) {
      if (hasTablePreference(tableName)) {
        return preference.value.table[tableName];
      }
      return null;
    }
    function migrateTablePreference(oldKey: string, newKey: string) {
      // Si la nouvelle clé existe déjà, pas besoin de migration
      if (hasTablePreference(newKey)) {
        return false;
      }

      // Si l'ancienne clé existe, migrer vers la nouvelle
      if (hasTablePreference(oldKey)) {
        const oldPreference = preference.value.table[oldKey];
        const { [oldKey]: _migratedPreference, ...remainingPreferences } = preference.value.table;
        preference.value.table = {
          ...remainingPreferences,
          [newKey]: oldPreference,
        };
        return true;
      }

      return false;
    }
    function updateThemePreference(theme: UserThemePreference) {
      preference.value = {
        ...normalizePreference(preference.value),
        theme: {
          ...preference.value.theme,
          ...theme,
        },
      };
    }

    return {
      preference,
      setPreference,
      updateTablePreference,
      removeTablePreference,
      getTablePreference,
      migrateTablePreference,
      updateThemePreference,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
    },
  },
);
